/* eslint-disable react/destructuring-assignment */
import React, { useCallback, useEffect, useState } from 'react'
import type { ExpandedChangedEvent } from 'ag-grid-community'
import type { CustomCellRendererProps } from 'ag-grid-react'
import { Button, Popover, Space, Typography } from 'antd'
import { LoadingOutlined, RightOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import {
  CustomReportVariable,
  ExplainerVariable,
  RowType
} from '../../../../../redux/context/customReports/typesVariable'
import { renderVariableTitle } from './utils/renderingUtils'
import { useVariableExplainer } from '../../../../../features/customReport/hooks/useVariableExplainer'
import { showErrorMessage } from '../../../../../utils/message'
import ReferenceRulesTable from '../../../../settings/company/keyFigures/formulas/components/ReferenceRules/ReferenceRulesTable'

type CustomGroupCellRendererProps = CustomCellRendererProps<
  CustomReportVariable | ExplainerVariable,
  any,
  { reportId: number; sectionId: number }
>

const CustomGroupCellContainer = styled.div<{ $paddingLeft: number; $fontWeight: string; $fontStyle: string }>`
  display: grid;
  grid-template-columns: 20px 1fr;
  align-items: center;
  padding-left: ${props => props.$paddingLeft}px;
  font-size: inherit;
  font-weight: ${props => props.$fontWeight};
  font-style: ${props => props.$fontStyle};
  width: 100%;
  gap: 2px;
  margin-left: -4px;
`

const flattenBalanceSheetData = (data: ExplainerVariable[]) => {
  const result: ExplainerVariable[] = []

  const processNode = (node: ExplainerVariable, parentId: string) => {
    // Create row data
    const row = {
      ...node,
      parentId: parentId || undefined,
      children: undefined
    } as ExplainerVariable

    result.push(row)

    // Process children recursively
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        processNode(child, node.id)
      })
    }
  }

  // Process each element of the json array
  if (data && Array.isArray(data)) {
    data.forEach(rootNode => {
      processNode(rootNode, rootNode.id.split('-')[0])
    })
  }

  return result
}

const CustomGroupCellRenderer: React.FC<CustomGroupCellRendererProps> = props => {
  const { node, data, context } = props
  const { reportId, sectionId } = context
  const [expanded, setExpanded] = useState(node.expanded)

  const { fetch, isLoading } = useVariableExplainer()

  useEffect(() => {
    const expandListener = (event: ExpandedChangedEvent) => setExpanded(event.node.expanded)

    node.addEventListener('expandedChanged', expandListener)
    return () => {
      node.removeEventListener('expandedChanged', expandListener)
    }
  }, [node])

  const onClick = useCallback(async () => {
    if (isLoading) return
    if (!node.expanded) {
      if (node.data?.id && node.level === 0 && !node.allChildrenCount) {
        try {
          // Fetch new data
          const { variables } = await fetch(reportId, sectionId, node.data.id as number)

          if (variables && Array.isArray(variables)) {
            // Add new rows to grid
            props.api.applyTransaction({ add: flattenBalanceSheetData(variables) })
            // Open current group
            node.setExpanded(true)
          } else {
            // If no new data, just toggle expand
            node.setExpanded(!node.expanded)
          }
        } catch (error) {
          console.error('Failed to fetch explainer data:', error)
          showErrorMessage('Failed to load expanded data. Please try again.')
          node.setExpanded(!node.expanded)
        }
      } else {
        // If no id, just toggle expand
        node.setExpanded(!node.expanded)
      }
    } else {
      node.setExpanded(!node.expanded)
    }
  }, [node, props.api, fetch, reportId, sectionId, isLoading])

  // Use renderTitle function if available, otherwise fallback
  const displayValue = renderVariableTitle(data as any, [])

  // Calculate indentation based on data or node level
  const indentLevel = (data?.style?.indentLevel || 0) + node.level
  const paddingLeft = Math.max(indentLevel * 8, 0)

  // Determine if row can be expanded - cannot expand account, empty or title rows
  const canExpand =
    (data?.params?.expandable === undefined || data?.params?.expandable === true) &&
    !data?.account &&
    (data && 'rowType' in data ? data.rowType !== RowType.empty && data.rowType !== RowType.title : true)

  return (
    <CustomGroupCellContainer
      $paddingLeft={paddingLeft}
      $fontWeight={data?.style?.fontWeight ? 'bold' : 'normal'}
      $fontStyle={data?.style?.fontStyle || 'normal'}
    >
      {/* Expand/Collapse arrow only if row can be expanded */}
      {canExpand ? (
        <Button
          type="text"
          size="small"
          onClick={onClick}
          icon={isLoading ? <LoadingOutlined /> : <RightOutlined rotate={expanded ? 90 : 0} />}
          style={{
            width: '20px',
            justifySelf: 'start' // Align left in grid cell
          }}
        />
      ) : (
        // Empty div spacer
        <div />
      )}

      <Popover
        content={
          <Space direction="vertical">
            <ReferenceRulesTable rules={data?.style?.referenceValueRules} />
            <Typography.Text code>{(data as any)?.source}</Typography.Text>
          </Space>
        }
      >
        {displayValue}
      </Popover>
    </CustomGroupCellContainer>
  )
}

export default CustomGroupCellRenderer
